import { createContext, createElement, useContext, useEffect, useState, type ReactNode } from 'react';
import { classifyDevice, type DeviceType } from '@cvsi/shared';

export type DeviceState = {
  type: DeviceType;
  viewportWidth: number;
  isPhone: boolean;
  isTablet: boolean;
  isPc: boolean;
};

const DeviceContext = createContext<DeviceState | null>(null);

function readDevice(): DeviceState {
  const viewportWidth = typeof window === 'undefined' ? 1024 : window.innerWidth;
  const type = classifyDevice(viewportWidth);
  return {
    type,
    viewportWidth,
    isPhone: type === 'phone',
    isTablet: type === 'tablet',
    isPc: type === 'pc',
  };
}

export function DeviceProvider({ children }: { children: ReactNode }) {
  const [device, setDevice] = useState<DeviceState>(readDevice);

  useEffect(() => {
    const update = () => setDevice(readDevice());
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    document.documentElement.dataset.device = device.type;
    document.documentElement.dataset.deviceClass = `device-${device.type}`;
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      delete document.documentElement.dataset.device;
      delete document.documentElement.dataset.deviceClass;
    };
  }, [device.type]);

  return createElement(DeviceContext.Provider, { value: device }, children);
}

export function useDevice(): DeviceState {
  const device = useContext(DeviceContext);
  if (!device) throw new Error('useDevice must be used inside DeviceProvider');
  return device;
}

